/**
 * WEB-672 phase 2a — <RichTextContent>: the single render path for RichField
 * values (string | RichSpan[]) across the public player, the phone preview
 * and the editor static preview.
 *
 * Invariants:
 * - IDENTITY PATH: a plain string renders as a bare text node — byte-identical
 *   to the legacy `{interp(block.content)}` output, no wrapper element. All
 *   pre-2a snapshots/tests stay valid.
 * - Every span text is a React TEXT CHILD (React escapes it). No
 *   dangerouslySetInnerHTML anywhere on this path — <script>/<img …> inside
 *   span.text or an interpolated answer renders as literal text.
 * - {{var}} interpolation goes through the existing player interpolate()
 *   per span.text, only when varCtx is provided (D7: href is NEVER
 *   interpolated and is additionally sanitized via safeHref at render).
 * - color: `token:<key>` resolves from the `colors` prop when given,
 *   otherwise from the theme CSS variables set on the render root; literals
 *   must pass the hex/rgb allowlist or the color is silently dropped.
 * - Link style decision (documented for WEB-672): an href span renders as
 *   <a target="_blank" rel="noopener noreferrer"> with the project's existing
 *   anchor look — accent color `var(--color-primary, #4094F7)` (same as the
 *   terms_checkbox / cookie-consent links) unless the span carries its own
 *   color, plus an explicit underline for affordance (merged with the strike
 *   mark when both apply).
 */
import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { RichField, RichSpan } from '@/types/rich-text';
import { resolveRichColor, richFieldToPlainText } from '@/types/rich-text';
import type { ThemeColors } from '@/themes/theme.types';
import type { VariableContext } from '@/player/variable-context';
import { interpolate } from '@/player/interpolate';
import { safeHref } from '@/utils/isAllowedUrl';

const LINK_COLOR = 'var(--color-primary, #4094F7)';

export interface RichTextContentProps {
  value: RichField | undefined | null;
  /** F6 interpolation context; omit to render {{var}} literally (editor previews). */
  varCtx?: VariableContext;
  /** Resolved theme colors for token:<key> spans; omit to resolve via CSS vars. */
  colors?: ThemeColors;
  /**
   * Rendered when the (interpolated) value is empty — parity with the legacy
   * `{interp(block.buttonText) || 'Continue'}` pattern.
   */
  fallback?: ReactNode;
}

function spanStyle(
  span: RichSpan,
  colors: ThemeColors | undefined,
  isLink: boolean,
): CSSProperties | undefined {
  const style: CSSProperties = {};
  const marks = span.marks ?? [];

  if (marks.includes('bold')) style.fontWeight = 700;
  if (marks.includes('italic')) style.fontStyle = 'italic';

  const decorations: string[] = [];
  // Links always carry an explicit underline (affordance) — merged with marks.
  if (isLink || marks.includes('underline')) decorations.push('underline');
  if (marks.includes('strike')) decorations.push('line-through');
  if (decorations.length > 0) style.textDecoration = decorations.join(' ');

  const color = resolveRichColor(span.color, colors);
  if (color !== undefined) {
    style.color = color;
  } else if (isLink) {
    style.color = LINK_COLOR;
  }

  return Object.keys(style).length > 0 ? style : undefined;
}

export function RichTextContent({
  value,
  varCtx,
  colors,
  fallback,
}: RichTextContentProps): React.ReactElement | null {
  const interp = (text: string): string => (varCtx ? interpolate(text, varCtx) : text);

  if (value == null) {
    return fallback !== undefined ? <>{fallback}</> : null;
  }

  // Identity path: plain string stays a bare text node, no wrapper.
  if (typeof value === 'string') {
    const text = interp(value);
    if (text === '') return fallback !== undefined ? <>{fallback}</> : null;
    return <>{text}</>;
  }

  const texts = value.map((span) => interp(span.text));
  if (texts.every((t) => t === '')) {
    return fallback !== undefined ? <>{fallback}</> : null;
  }

  return (
    <>
      {value.map((span, i) => {
        const text = texts[i];
        if (text === '') return null;
        if (span.href) {
          return (
            <a
              key={i}
              href={safeHref(span.href)}
              target="_blank"
              rel="noopener noreferrer"
              style={spanStyle(span, colors, true)}
            >
              {text}
            </a>
          );
        }
        return (
          <span key={i} style={spanStyle(span, colors, false)}>
            {text}
          </span>
        );
      })}
    </>
  );
}
